import { useNavigate } from "react-router-dom";
import { useCatalog } from "../app/CatalogContext";
import { ROUTES } from "../app/routes";
import { useI18n } from "../i18n/I18nProvider";
import {
  Badge,
  Button,
  Card,
  CodeBlock,
  DocsIcon,
  IntegrationIcon,
  KeyIcon,
  ModelsIcon,
  ParamsIcon,
  PipelineIcon,
  typographyClass,
} from "../ui-kit";

/** Квикстарт: тот же путь, что описан в принципах ниже, только кодом. */
const quickstart = (t: (key: string) => string) =>
  [
    t("quickstart.step1"),
    'curl "$PORTAL_URL/api/models"',
    "",
    t("quickstart.step2"),
    'curl "$PORTAL_URL/api/models/gemini-image"',
    "",
    t("quickstart.step3"),
    'curl -X POST "$PORTAL_URL/api/models/gemini-image" \\',
    "  -H 'Content-Type: application/json' \\",
    '  -H "X-Api-Key: $PHYGITAL_API_KEY" \\',
    `  -d '{"inputs":{"text_prompt":"a ripe banana"},"params":{}}'`,
    "",
    t("quickstart.step4"),
    'curl "$PORTAL_URL/api/tasks/179195" -H "X-Api-Key: $PHYGITAL_API_KEY"',
  ].join("\n");

/** Три причины пользоваться API — иконка задаёт тему карточки. */
const VALUE_CARDS = [
  { key: "models", Icon: ModelsIcon },
  { key: "contract", Icon: IntegrationIcon },
  { key: "params", Icon: ParamsIcon },
] as const;

/** Принципы работы: порядок = порядок вызовов, поэтому список нумерованный. */
const PRINCIPLES = [
  { key: "catalog", Icon: DocsIcon },
  { key: "async", Icon: PipelineIcon },
  { key: "polling", Icon: ParamsIcon },
  { key: "key", Icon: KeyIcon },
] as const;

/**
 * Витрина портала: зачем нужен API и как он устроен.
 *
 * Первый пункт бокового меню («Обзор»): сюда приходят до того, как знают
 * названия ручек, поэтому главные выходы отсюда — «документация» и «каталог
 * моделей», а сайдбар рядом показывает, что ещё есть в портале.
 */
export function HomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { catalog } = useCatalog();

  return (
    <div className="home">
      <Card className="home-hero">
        <span className="home-eyebrow">
          <Badge tone="grey" size="small">
            {t("home.eyebrow")}
          </Badge>
        </span>
        <h1 className={typographyClass("header-30-semibold")}>{t("home.title")}</h1>
        <p className={`home-lead ${typographyClass("text-14-medium")}`}>{t("home.lead")}</p>

        <div className="home-actions">
          <Button onClick={() => navigate(ROUTES.overview)}>{t("home.cta.docs")}</Button>
          <Button variant="tertiary" onClick={() => navigate(ROUTES.catalog)}>
            {t("home.cta.catalog")}
          </Button>
          {catalog && (
            <Badge tone="grey" size="small">
              {t("home.modelCount", { count: catalog.items.length })}
            </Badge>
          )}
        </div>
      </Card>

      <section className="home-section">
        <h2 className={typographyClass("header-20-semibold")}>{t("home.value.title")}</h2>
        <div className="home-grid">
          {VALUE_CARDS.map(({ key, Icon }) => (
            <Card key={key} className="home-card">
              <span className="home-card__icon" aria-hidden="true">
                <Icon />
              </span>
              <h3 className={typographyClass("header-16-semibold")}>{t(`home.value.${key}.title`)}</h3>
              <p className={`home-card__text ${typographyClass("caption-13-medium")}`}>
                {t(`home.value.${key}.text`)}
              </p>
            </Card>
          ))}
        </div>
      </section>

      <section className="home-section">
        <h2 className={typographyClass("header-20-semibold")}>{t("home.how.title")}</h2>
        <p className={`home-section__lead ${typographyClass("text-14-medium")}`}>
          {t("home.how.lead")}
        </p>

        <div className="home-how">
          <ol className="home-principles">
            {PRINCIPLES.map(({ key, Icon }, index) => (
              <li key={key}>
                <span className="home-principles__icon" aria-hidden="true">
                  <Icon />
                </span>
                <h3 className={typographyClass("text-14-semibold")}>
                  {index + 1}. {t(`home.how.${key}.title`)}
                </h3>
                <p className={`home-card__text ${typographyClass("caption-13-medium")}`}>
                  {t(`home.how.${key}.text`)}
                </p>
              </li>
            ))}
          </ol>

          <CodeBlock code={quickstart(t)} language="cURL" numbered />
        </div>
      </section>

      <Card className="home-footer">
        <div>
          <h2 className={typographyClass("header-16-semibold")}>{t("home.next.title")}</h2>
          <p className={`home-card__text ${typographyClass("caption-13-medium")}`}>
            {t("home.next.text")}
          </p>
        </div>
        <Button onClick={() => navigate(ROUTES.overview)}>{t("home.cta.docs")}</Button>
      </Card>
    </div>
  );
}
